import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/pro-regular-svg-icons";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { useAuth, Roles } from "../../providers/AuthContext";
import {
  getChargingPoint,
  updateChargingPoint,
  createChargingPoint,
  getChargingPointThirdPartySystemMapping,
  addChargingPointThirdPartySystemMapping,
  deleteChargingPointThirdPartySystemMapping
} from "../../api/ChargingPoint";
import { getManufacturers } from "../../api/Manufacturer"; 
import toast from "react-hot-toast";
import LoadingTablePage from "../LoadingTablePage";
import ConfirmationModal from "../../components/ConfirmationModal";
import CongifurationForm from "../../components/point/ConfigurationForm";
import EvseSettingsForm from "../../components/point/EvseSettingsForm";

import EditPointDetails from "../../components/point/EditPointDetails";
import ScheduledRebootForm, { buildRebootSchedulePayload } from "../../components/point/ScheduledRebootForm";
import RemoteManagement from "../../components/point/RemoteManagement";
import PointCertificates from "../../components/point/PointCertificates";
import {
  post_RemoteManagement_reset,
  post_RemoteManagement_clear_cache,
  post_RemoteManagement_data_transfer,
  post_RemoteManagement_trigger_message,
} from "../../api/Ocpp";
import { post_RemoteManagement_reset as post_RemoteManagement_reset_201, post_RemoteManagement_trigger_message as post_RemoteManagement_trigger_message_201 } from "../../api/Ocpp201";
import PointConnectors from "../../components/point/PointConnectors";
import PointStatusLog from "../../components/point/PointStatusLog";
import { getAllChargingSites, getChargingSiteParkingSpaces } from "../../api/ChargingSites";
import PointThirdPartyRef from "../../components/point/PointThirdPartyRef";
import GetDiagnosticsModal from "../../components/diagnostics/GetDiagnosticsModal";
import { isOcpp201, hasBasicAuthPassword, requiresBasicAuthPasswordOnUpdate } from "../../components/point/ocppPointHelpers";
import { useTranslation } from "react-i18next";

const CPMS_LIGHT_HIDDEN_TABS = ["references", "certificates"];

const EditChargingPointsPage = () => {
  const { t } = useTranslation();
  const { getAccessToken, hasRole } = useAuth();
  const isAdmin = hasRole(Roles.Admin);
  const showPointRestrictedTabs = isAdmin;
  const { id } = useParams();
  const location = useLocation();
  const { chargingSitePublicId } = location.state || {};

  const navigate = useNavigate();
  const [chargingSites, setChargingSites] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [parkingSpaces, setParkingSpaces] = useState([]);

  const [chargingPoint, setChargingPoint] = useState(null);
  const [originalPoint, setOriginalPoint] = useState(null);

  const [thirdPartySystemMapping, setThirdPartySystemMapping] = useState([]);

  const [loadingPoint, setLoadingPoint] = useState(true);
  const [confirmState, setConfirmState] = useState({ isOpen: false, id: "" });
  const [isGetDiagnosticsOpen, setIsGetDiagnosticsOpen] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const query = new URLSearchParams(location.search);
  const [activeTab, setActiveTab] = useState(
    query.get("tab") ? query.get("tab") : "details"
  );
  const [invalidFields, setInvalidFields] = useState([]);

  console.log("Point", chargingPoint);

  useEffect(() => {
    const tab = query.get("tab");
    if (tab) {
      if (!showPointRestrictedTabs && CPMS_LIGHT_HIDDEN_TABS.includes(tab)) {
        setActiveTab("details");
        return;
      }
      if (tab === "certificates" && chargingPoint && !isOcpp201(chargingPoint.ocppVersion)) {
        setActiveTab("details");
        return;
      }
      setActiveTab(tab);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
  }, [location.search, showPointRestrictedTabs, chargingPoint?.ocppVersion]);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    query.set("tab", activeTab);
    navigate(`${location.pathname}?${query.toString()}`, {
      replace: true,
      state: {
        chargingSitePublicId: chargingSitePublicId,
      },
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
  }, [activeTab, location.pathname, navigate]);

  useEffect(() => {
    const fetchChargingSites = async () => {
      try {
        const accessToken = await getAccessToken();
        const sites = await getAllChargingSites(accessToken); // Assuming this API returns a list of charging sites
        setChargingSites(sites); // Store charging sites in the state
      } catch (error) {
        toast.error(t("toast.failedFetch", { entity: t("entity.sites") }));
      } finally {
      }
    };

    fetchChargingSites();
  }, [getAccessToken]);

  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const accessToken = await getAccessToken();
        const vendors = await getManufacturers(accessToken);
        setVendors(vendors);
      } catch (error) {
        toast.error(t("toast.failedFetch", { entity: t("common.vendor") }));
      }
    };

    fetchVendors();
  }, [getAccessToken]);

  useEffect(() => {
    if (id !== "0") {
      setLoadingPoint(true);
      handleGetChargingPoint();
      if (showPointRestrictedTabs) {
        handleGetChargingPointThirdPartySystemMapping();
      }
    } else {
      setChargingPoint({
        chargingPointPublicId: "",
        chargingPointName: "",
        chargingSitePublicId: "",
        chargingSiteName: "",
        chargingPointModel: "",
        chargingPointVendor: "",
        protocolType: "OCPP",
        isMultiStationChargingPoint: false, // CHANGE: default boolean
        supportsReservation: false,
        supportsGetDiagnostics: true,
        parkings: [],

      });
      setLoadingPoint(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
  }, [id, showPointRestrictedTabs]);

  const toBool = (mscp) => {
    if (typeof mscp === "boolean") return mscp;
    if (typeof mscp === "number") return mscp !== 0;
    if (typeof mscp === "string") return mscp.toLowerCase() === "true";
    return false; // default
  };

  /*   useEffect(() => {
  if (chargingPoint) {
    handleGetDiagnostics();
  }
}, [chargingPoint]); */

  const handleGetChargingPointThirdPartySystemMapping = async () => {
    try {
      const accessToken = await getAccessToken();
      const mapping = await getChargingPointThirdPartySystemMapping(id, accessToken);
      setThirdPartySystemMapping(mapping);
    } catch (error) {
      toast.error(t("toast.failedFetch", { entity: t("tabs.references") }), error);
    }
  };

  const handleGetChargingPoint = async () => {
      try {
          const accessToken = await getAccessToken();
          const pointData = await getChargingPoint(id, accessToken);


          const normalized = {
              ...pointData,

              isMultiStationChargingPoint: toBool(
                  pointData.isMultiStationChargingPoint
              ),
              supportsReservation: toBool(pointData.supportsReservation),
              supportsGetDiagnostics:
                pointData.supportsGetDiagnostics ?? true,

              ocppVersion: pointData.ocppVersion ?? "",
              basicAuthPassword: pointData.basicAuthPassword ?? "",

              evseCapabilities: pointData.evseCapabilities ?? pointData.capabilities ?? [],
              directions: pointData.directions ?? [],
              parkingRestrictions:
                  pointData.parkingRestrictions ?? [],
              parkings: pointData.parkings ?? [],
              statusSchedules:
                  pointData.statusSchedules ?? [],
              floorLevel: pointData.floorLevel ?? "",
              physicalReference:
                  pointData.physicalReference ?? "",
              latitude: pointData.latitude ?? "",
              longitude: pointData.longitude ?? "",
              acceptedServiceProviders: pointData.acceptedServiceProviders ?? [],
              rebootWeekdays: Array.isArray(pointData.rebootWeekdays)
                ? pointData.rebootWeekdays
                    .map((weekday) => Number(weekday))
                    .filter((weekday) => Number.isInteger(weekday) && weekday >= 0 && weekday <= 6)
                : undefined,
              rebootTimeLocal: pointData.rebootTimeLocal ?? "",


        connectors: pointData.connectors ?? pointData.chargingConnectors ?? [],
      };

      setChargingPoint(normalized);
      setOriginalPoint(normalized);
      setLoadingPoint(false);
      /*  handleGetDiagnostics(pointData); */
    } catch (error) {
      toast.error(t("toast.failedFetch", { entity: t("entity.chargingPoint") }));
      setLoadingPoint(false);
    }
  };

  useEffect(() => {
    const fetchParkingSpaces = async () => {
      const sitePublicId = chargingPoint?.chargingSitePublicId;
      if (!sitePublicId) {
        setParkingSpaces([]);
        return;
      }

      try {
        const accessToken = await getAccessToken();
        const spaces = await getChargingSiteParkingSpaces(sitePublicId, accessToken);
        setParkingSpaces(spaces || []);
      } catch (error) {
        toast.error(error?.message || t("toast.failedFetch", { entity: t("entity.parking") }));
        setParkingSpaces([]);
      }
    };

    fetchParkingSpaces();
  }, [chargingPoint?.chargingSitePublicId, getAccessToken]);

  const handleUpdateChargingPoint = async () => {
    const {
      chargingPointName,
      chargingSitePublicId,
      chargingPointModel,
        chargingPointVendor,
        chargingPointConnectionId,
      acceptedServiceProviders,
      ocppVersion,
      basicAuthPassword,
    } = chargingPoint;

    const missing = [];
    if (!chargingPointName) missing.push("chargingPointName");
    if (!chargingSitePublicId) missing.push("chargingSitePublicId");
    if (!chargingPointModel) missing.push("chargingPointModel");
    if (!chargingPointVendor) missing.push("chargingPointVendor");
    if (!chargingPointConnectionId) missing.push("chargingPointConnectionId");
    if (!ocppVersion) missing.push("ocppVersion");
    if (requiresBasicAuthPasswordOnUpdate(ocppVersion, originalPoint?.ocppVersion, basicAuthPassword)) {
      missing.push("basicAuthPassword");
    }

    const rebootSchedule = buildRebootSchedulePayload(chargingPoint);
    if (rebootSchedule?.timeMissing) {
      missing.push("rebootTimeLocal");
    }

    if (missing.length > 0) {
      setInvalidFields(missing);
      toast.error(
        missing.includes("basicAuthPassword")
          ? t("point.basicAuthRequired")
          : missing.includes("rebootTimeLocal") && missing.length === 1
            ? t("point.scheduledReboot.timeRequired")
            : t("common.pleaseFillRequiredFields", { fields: missing.join(", ") })
      );
      return;
    }

    setInvalidFields([]);

    const toastId = toast.loading(t("toast.updating", { entity: t("entity.chargingPoint") }));

    try {
      const accessToken = await getAccessToken();

      const toIsoOrNull = (v) => (v ? new Date(v).toISOString() : null);
      const evseCapabilities =
        chargingPoint.evseCapabilities ?? chargingPoint.capabilities ?? [];
      const normalizedEvseCapabilities = chargingPoint.supportsReservation
        ? evseCapabilities
        : evseCapabilities.filter((capability) => capability !== "RESERVABLE");

      const updatedChargingPointData = {
        chargingPointPublicId: chargingPoint.chargingPointPublicId || "",
        chargingPointName: chargingPoint.chargingPointName || "",
        chargingPointVendor: chargingPoint.chargingPointVendor || "",
        chargingPointModel: chargingPoint.chargingPointModel || "",
        chargingPointConnectionId: chargingPoint.chargingPointConnectionId || "",
        ocppVersion: chargingPoint.ocppVersion || "",
        isMultiStationChargingPoint:
          typeof chargingPoint.isMultiStationChargingPoint === "boolean"
            ? chargingPoint.isMultiStationChargingPoint
                  : undefined,
        supportsReservation:
          typeof chargingPoint.supportsReservation === "boolean"
            ? chargingPoint.supportsReservation
            : undefined,

        evseCapabilities: normalizedEvseCapabilities,
        floorLevel: chargingPoint.floorLevel ?? "",
        physicalReference: chargingPoint.physicalReference ?? "",
        latitude: chargingPoint.latitude ?? "",
        longitude: chargingPoint.longitude ?? "",
        acceptedServiceProviders: acceptedServiceProviders ?? [],

        directions: Array.isArray(chargingPoint.directions)
          ? chargingPoint.directions.map((d) => ({
              language: d.language ?? "",
              text: d.text ?? "",
            }))
          : undefined,

        parkingRestrictions: chargingPoint.parkingRestrictions ?? [],
        parkings: Array.isArray(chargingPoint.parkings)
          ? chargingPoint.parkings.map((parking) => ({
              parkingPublicId: parking.parkingPublicId ?? "",
              evsePosition: parking.evsePosition ?? "",
            }))
          : [],

        statusSchedules: Array.isArray(chargingPoint.statusSchedules)
          ? chargingPoint.statusSchedules.map((s) => ({
              status: s.status ?? "",
              periodBeginUtc: toIsoOrNull(s.periodBeginUtc),
              periodEndUtc: toIsoOrNull(s.periodEndUtc),
            }))
          : [],
      };

      if (rebootSchedule) {
        updatedChargingPointData.rebootWeekdays = rebootSchedule.rebootWeekdays;
        updatedChargingPointData.rebootTimeLocal = rebootSchedule.rebootTimeLocal;
      }

      if (!isOcpp201(ocppVersion)) {
        updatedChargingPointData.basicAuthPassword = null;
      } else if (
        hasBasicAuthPassword(basicAuthPassword)
        && String(basicAuthPassword) !== String(originalPoint?.basicAuthPassword ?? "")
      ) {
        updatedChargingPointData.basicAuthPassword = basicAuthPassword;
      } else if (requiresBasicAuthPasswordOnUpdate(ocppVersion, originalPoint?.ocppVersion, basicAuthPassword)) {
        updatedChargingPointData.basicAuthPassword = basicAuthPassword || "";
      }

      const updatedChargingPoint = await updateChargingPoint(
        updatedChargingPointData,
        accessToken
      );

      if (showPointRestrictedTabs) {
        const thirdPartySystemPromises = thirdPartySystemMapping.map(async (thirdPartySystem) => {
          if (thirdPartySystem.isNew) {
            thirdPartySystem.chargingPointPublicId = updatedChargingPoint.chargingPointPublicId;
            return addChargingPointThirdPartySystemMapping(thirdPartySystem, accessToken);
          } else if (thirdPartySystem.isDeleted) {
            return deleteChargingPointThirdPartySystemMapping(
              thirdPartySystem.chargingPointPublicId,
              thirdPartySystem.thirdPartySystemPublicId,
              thirdPartySystem.thirdPartySystemExternalId,
              accessToken
            );
          }
        });

        await Promise.all(thirdPartySystemPromises);
      }

      toast.success(t("toast.updated", { entity: t("entity.chargingPoint") }), { id: toastId });

      const next = updatedChargingPoint ?? {
        ...chargingPoint,
        ...updatedChargingPointData,
        parkingRestrictions: updatedChargingPointData.parkingRestrictions,
        statusSchedules: updatedChargingPointData.statusSchedules,
        floorLevel: updatedChargingPointData.floorLevel,
        physicalReference: updatedChargingPointData.physicalReference,
        latitude: updatedChargingPointData.latitude,
        longitude: updatedChargingPointData.longitude,
        acceptedServiceProviders: updatedChargingPointData.acceptedServiceProviders,
      };

      setChargingPoint(next);
      setOriginalPoint(next);

      setHasChanges(false);

      toast.success(t("toast.updated", { entity: t("entity.chargingPoint") }), { id: toastId });
    } catch (error) {
      console.error(
        "Error response from API:",
        error.response ? error.response.data : error
      );
        toast.error(error?.message || t("toast.failedUpdate", { entity: t("entity.chargingPoint") }), {
        id: toastId,
      });
    }
  };

  const handleReset = async () => {
    try {
      const accessToken = await getAccessToken();
      const payload = {
        chargingPointPublicId: chargingPoint.chargingPointPublicId,
        type: chargingPoint.type || "Hard",
      };

      if (isOcpp201(chargingPoint.ocppVersion)) {
        await post_RemoteManagement_reset_201(accessToken, payload);
      } else {
        await post_RemoteManagement_reset(accessToken, payload);
      }
      toast.success(t("point.resetSent"));
    } catch (error) {
      console.error(error);
      toast.error(error?.message || t("toast.failedSave"));
    }
  };

  const handleClearCache = async () => {
    if (isOcpp201(chargingPoint.ocppVersion)) {
      toast.error(t("remote.clearCacheUnavailable") + ".");
      return;
    }

    try {
      const accessToken = await getAccessToken();
      await post_RemoteManagement_clear_cache(accessToken, {
        chargingPointPublicId: chargingPoint.chargingPointPublicId,
      });
      toast.success(t("point.clearCacheSent"));
    } catch (error) {
      console.error(error);
      toast.error(error?.message || t("common.failedToSave"));
    }
  };

  const handleDataTransfer = async () => {
    if (isOcpp201(chargingPoint.ocppVersion)) {
      toast.error(t("point.dataTransferUnavailable"));
      return;
    }

    try {
      const accessToken = await getAccessToken();
      await post_RemoteManagement_data_transfer(accessToken, {
        chargingPointPublicId: chargingPoint.chargingPointPublicId,
        vendorName: chargingPoint.chargingPointVendor || "",
        messageName: chargingPoint.messageName || "",
        data: chargingPoint.data || "",
      });
      toast.success(t("point.dataTransferSent"));
    } catch (error) {
      console.error(error);
      toast.error(error?.message || t("toast.failedSave"));
    }
  };

  const handleTriggerMessage = async () => {
    if (!chargingPoint.triggerMessage) {
      toast.error(t("point.selectTriggerMessage"));
      return;
    }

    try {
      const accessToken = await getAccessToken();
      const payload = {
        chargingPointPublicId: chargingPoint.chargingPointPublicId,
        chargingConnectorPublicId:
          chargingPoint.chargingConnectorPublicId || "",
        requestedMessage: chargingPoint.triggerMessage || "",
      };

      if (isOcpp201(chargingPoint.ocppVersion)) {
        await post_RemoteManagement_trigger_message_201(accessToken, payload);
      } else {
        await post_RemoteManagement_trigger_message(accessToken, payload);
      }
      toast.success(t("point.triggerSent"));
    } catch (error) {
      console.error(error);
      toast.error(error?.message || t("toast.failedSave"));
    }
  };

  const saveAndExit = () => {
    if (chargingPoint.chargingPointPublicId) {
      console.log("Updating point...");

      handleUpdateChargingPoint();
    } else {
      console.log("Creating point...");

      handleCreateChargingPoint();
    }
  };

  const handleCreateChargingPoint = async () => {
    const toastId = toast.loading(t("toast.creating", { entity: t("entity.chargingPoint") }));
    try {
      const accessToken = await getAccessToken();
      const newPoint = await createChargingPoint(chargingPoint, accessToken);
      setChargingPoint(newPoint);
      toast.success(t("toast.created", { entity: t("entity.chargingPoint") }), { id: toastId });
      setHasChanges(false);
    } catch (error) {
      toast.error(t("toast.failedCreate", { entity: t("entity.chargingPoint") }), { id: toastId });
    }
  };
  const handleBackClick = () => {
    if (hasChanges) {
      setConfirmState((s) => ({
        ...s,
        isOpen: true,
        title: t("confirm.discard"),
        confirm: () => {
          if (chargingSitePublicId) {
            navigate(`/site/${chargingSitePublicId}`);
          } else if (chargingSitePublicId) {
            navigate(`/site/${chargingSitePublicId}`);
          } else {
            navigate("/points");
          }
        },
      }));
    } else {
      if (chargingSitePublicId) {
        navigate(`/site/${chargingSitePublicId}`);
      } else if (chargingSitePublicId) {
        navigate(`/site/${chargingSitePublicId}`);
      } else {
        navigate("/points");
      }
    }
  };

  const isEqualDeep = (a, b) => JSON.stringify(a) === JSON.stringify(b);

  const handleInputChange = (key, value) => {
    setChargingPoint((prev) => {
      const next = { ...prev, [key]: value };
      if (originalPoint) {
        setHasChanges(!isEqualDeep(next, originalPoint));
      }
      return next;
    });
  };

  const handleSupportsReservationChange = (value) => {
    if (chargingPoint?.supportsReservation && value === false) {
        setConfirmState((s) => ({
            ...s,
            isOpen: 
                true,
                title: t("confirm.disableReservationTitle"),
                okText: t("confirm.disableReservationOk"),
        confirm: () => handleInputChange("supportsReservation", false),
      }));
      return;
    }

    handleInputChange("supportsReservation", value);
  };

  const handleInputChangeNoSave = (key, value) => {
    setChargingPoint((prev) => ({ ...prev, [key]: value }));
  };


  return (
    <>
      <HeaderBar />
      <div className="flex flex-row w-full">
        <Sidebar />
        <main className="page">
          <div className="absolute inset-0 pointer-events-none">
            <LoadingTablePage title={t("pages.chargingPoints")} loading={loadingPoint} />
          </div>

          <div className={`${loadingPoint ? "loading" : ""} page-content`}>
            <div className="flex flex-row items-center pb-4">
              <button onClick={handleBackClick} className="text-black flex ">
                <FontAwesomeIcon icon={faArrowLeft} className="w-4 pr-4" />
              </button>
              <h2 className="border-l border-mute-200 pl-4 flex items-center gap-4">
                {id !== "0" ? (
                  <>
                    {chargingPoint?.chargingPointName}
                    <StatusBadge
                      status={chargingPoint?.isOnline ? "Online" : "Offline"}
                      label={chargingPoint?.isOnline ? t("common.online") : t("common.offline")}
                    />
                  </>
                ) : (
                  t("pages.createPoint")
                )}
              </h2>
              <div className="flex gap-4 ml-auto">
                <button
                  className="btn btn-outline-primary"
                  onClick={handleBackClick}
                >
                  {t("common.cancel")}
                </button>
                <button
                  className="btn btn-primary"
                  disabled={!hasChanges}
                  onClick={() => {
                    saveAndExit();
                  }}
                >
                  {t("common.save")}
                </button>
              </div>
            </div>

            {/* Charging point Form */}
            <div className="flex flex-col px-4 py-8 shadow-lg rounded-md w-full">
              <div className="border-b border-mute-200 mb-4 gap-4 flex">
                <button
                  className={`px-4 pb-2 ${
                    activeTab === "details"
                      ? "border-b-2 border-black font-bold text-sm text-black"
                      : "text-mute-400 text-sm font-bold"
                  }`}
                  onClick={() => setActiveTab("details")}
                >
                  {t("tabs.details")}
                </button>
                {showPointRestrictedTabs && (
                  <button
                    className={`px-4 pb-2 ${activeTab === "references"
                      ? "border-b-2 border-black font-bold text-sm text-black"
                      : "text-mute-400 text-sm font-bold"
                      }`}
                    onClick={() => setActiveTab("references")}
                  >
                    {t("tabs.references")}
                  </button>
                )}
                <button
                  className={`px-4 pb-2 ${
                    activeTab === "configuration"
                      ? "border-b-2 border-black font-bold text-sm text-black"
                      : "text-mute-400 text-sm font-bold"
                  }`}
                  onClick={() => setActiveTab("configuration")}
                >
                  {t("tabs.configuration")}
                </button>
                <button
                  className={`px-4 pb-2 ${
                    activeTab === "connectors"
                      ? "border-b-2 border-black font-bold text-sm text-black"
                      : "text-mute-400 text-sm font-bold"
                  }`}
                  onClick={() => setActiveTab("connectors")}
                >
                  {t("tabs.connectors")}
                </button>
                <button
                  className={`px-4 pb-2 ${
                    activeTab === "events"
                      ? "border-b-2 border-black font-bold text-sm text-black"
                      : "text-mute-400 text-sm font-bold"
                  }`}
                  onClick={() => setActiveTab("events")}
                >
                  {t("tabs.events")}
                </button>
                              {/*  <button
                  className={`px-4 pb-2 ${
                    activeTab === "information"
                      ? "border-b-2 border-black font-bold text-sm text-black"
                      : "text-mute-400 text-sm font-bold"
                  }`}
                  onClick={() => setActiveTab("information")}
                >
                  Information
                </button> */}
                <button
                  className={`px-4 pb-2 ${
                    activeTab === "remoteManagement"
                      ? "border-b-2 border-black font-bold text-sm text-black"
                      : "text-mute-400 text-sm font-bold"
                  }`}
                  onClick={() => setActiveTab("remoteManagement")}
                >
                  {t("tabs.remoteManagement")}
                </button>
                {showPointRestrictedTabs && isOcpp201(chargingPoint?.ocppVersion) && (
                  <button
                    className={`px-4 pb-2 ${
                      activeTab === "certificates"
                        ? "border-b-2 border-black font-bold text-sm text-black"
                        : "text-mute-400 text-sm font-bold"
                    }`}
                    onClick={() => setActiveTab("certificates")}
                  >
                    {t("tabs.certificates")}
                  </button>
                )}

                {!toBool(chargingPoint?.isMultiStationChargingPoint) && (
                  <button
                    className={`px-4 pb-2 ${
                      activeTab === "evseSettings"
                        ? "border-b-2 border-black font-bold text-sm text-black"
                        : "text-mute-400 text-sm font-bold"
                    }`}
                    onClick={() => setActiveTab("evseSettings")}
                  >
                    {t("tabs.evseSettings")}
                  </button>
                )}
              </div>

                {activeTab === "details" && chargingPoint && (
                    <div className="w-full">
                        <EditPointDetails
                            chargingPoint={chargingPoint}
                            handleInputChange={handleInputChange}
                            handleSupportsReservationChange={handleSupportsReservationChange}
                            chargingSites={chargingSites}
                            invalidFields={invalidFields}
                            isEdit={chargingPoint.chargingPointPublicId !== "0"}
                        />
                    </div>
                )}

              {activeTab === "configuration" && (
                <CongifurationForm
                  chargingPoint={chargingPoint}
                  handleInputChange={handleInputChange}
                  setHasChanges={setHasChanges}
                />
              )}

              {activeTab === "events" && <PointStatusLog pointPublicId={id} />}

              {activeTab === "connectors" && <PointConnectors pointId={id} />}

                          { /*  {activeTab === "information" && chargingPoint && (
                <PointInformation chargingPoint={chargingPoint} />
              )}*/}
              {activeTab === "remoteManagement" && chargingPoint && (
                <div className="flex w-full flex-col gap-8">
                  {id !== "0" && (
                    <ScheduledRebootForm
                      chargingPoint={chargingPoint}
                      handleInputChange={handleInputChange}
                      invalidFields={invalidFields}
                    />
                  )}
                  {showPointRestrictedTabs && (
                    <RemoteManagement
                      chargingPoint={chargingPoint}
                      resetPoint={handleReset}
                      clearCache={handleClearCache}
                      dataTransfer={handleDataTransfer}
                      triggerMessage={handleTriggerMessage}
                      handleInputChange={handleInputChangeNoSave}
                      vendors={vendors}
                    />
                  )}
                </div>
              )}
              {showPointRestrictedTabs &&
                isOcpp201(chargingPoint?.ocppVersion) &&
                activeTab === "certificates" &&
                chargingPoint && (
                <PointCertificates chargingPoint={chargingPoint} />
              )}
              {activeTab === "evseSettings" && chargingPoint && (
                <EvseSettingsForm
                  chargingPoint={chargingPoint}
                  invalidFields={invalidFields}
                  handleInputChange={(key, value) => {
                    setChargingPoint((prev) => ({ ...prev, [key]: value }));
                    setHasChanges(true);
                  }}
                  parkingSpaces={parkingSpaces}
                />
              )}

              {showPointRestrictedTabs && activeTab === "references" && (
                <div className="flex w-full mt-4">
                  <PointThirdPartyRef
                    thirdPartySystemMapping={thirdPartySystemMapping}
                    setThirdPartySystemMapping={setThirdPartySystemMapping}
                    setHasChanges={setHasChanges} />
                </div>
              )}
            </div>
          </div>
        </main>

        {/* Modals */}
        <ConfirmationModal
          isOpen={confirmState.isOpen}
          title={confirmState.title}
          okText={confirmState.okText}
          onClose={() =>
            setConfirmState((s) => ({ ...s, isOpen: false, okText: undefined }))
          }
          onConfirm={() => {
            setConfirmState((s) => ({ ...s, isOpen: false, okText: undefined }));
            confirmState.confirm();
          }}
        />
        <GetDiagnosticsModal
          isOpen={isGetDiagnosticsOpen}
          onClose={() => setIsGetDiagnosticsOpen(false)}
          chargingPointPublicId={chargingPoint?.chargingPointPublicId}
          chargingPointName={chargingPoint?.chargingPointName}
          ocppVersion={chargingPoint?.ocppVersion}
          supportsGetDiagnostics={
            chargingPoint?.supportsGetDiagnostics ?? true
          }
        />
      </div>
    </>
  );
};

export default EditChargingPointsPage;

const StatusBadge = ({ status, label }) => {
  const statusBGColor = {
    Online: "bg-secondary-100",
    Offline: "bg-error-100",
  };

  const statusFontColor = {
    Online: "text-secondary-500",
    Offline: "text-error-500",
  };

  return (
    <span
      className={`px-2 py-1 rounded-md ${statusFontColor[status]} text-xs font-bold ${statusBGColor[status]}`}
    >
      {label ?? status}
    </span>
  );
};

